// r24 Product Catalog — Notary A5 product page renderer (parallel of NaatiProductPage).
// Composes hero + trust strip + long desc + tiers + accepted-by + process + FAQ + related.
// Consumed by /notary/products/$slug (TH) and /en/notary/products/$slug (EN).

import ProductDepthBlock from "@/components/ProductDepthBlock";
import { Link } from "@tanstack/react-router";
import { Check, Clock, ShieldCheck, Stamp, Globe2, ArrowRight, Scale } from "lucide-react";
import type { NotaryProduct } from "@/data/notary-products";
import { NOTARY_LEAD_ATTORNEY, relatedNotaryProducts } from "@/data/notary-products";
import NotaryProductCTA from "./NotaryProductCTA";
import NotaryAuthorByline from "./NotaryAuthorByline";
import { CrossSellStrip } from "@/components/CrossSellStrip";
import ConsultantAdvisoryBlock from "@/components/ConsultantAdvisoryBlock";

interface Props {
  product: NotaryProduct;
  locale: "th" | "en";
  faqs: { q: string; a: string }[];
}

export default function NotaryProductPage({ product, locale, faqs }: Props) {
  const isTh = locale === "th";
  const name = isTh ? product.nameTh : product.nameEn;
  const catLabel = isTh ? product.categoryLabelTh : product.categoryLabelEn;
  const longDesc = isTh ? product.longDescTh : product.longDescEn;
  const bullets = isTh ? product.bulletTh : product.bulletEn;
  const steps = isTh ? product.processStepsTh : product.processStepsEn;
  const related = relatedNotaryProducts(product.slug, 6);

  const L = isTh ? {
    trust: `รับรองโดย ${NOTARY_LEAD_ATTORNEY} — จดทะเบียนสภาทนายความไทย`,
    priceLabel: "ค่าบริการต่อชุด",
    turnLabel: "ระยะเวลา",
    acceptedBy: "ยอมรับโดย",
    usedFor: "ใช้ยื่นได้กับ",
    whatIncluded: "รวมในราคานี้",
    tiers: "ทางเลือกการนัดหมาย",
    tierStd: "มาตรฐาน — 1 วัน", tierExp: "เร่งด่วน — วันเดียว", tierOn: "นอกสถานที่ / On-site",
    tierStdDesc: "นัดล่วงหน้า 1 วันทำการ — ยอดนิยม", tierExpDesc: "รับ walk-in ในวันเดียวหากมีคิวว่าง", tierOnDesc: "ทนายเดินทางไปหาที่โรงพยาบาล / โรงแรม / บริษัท (พื้นที่ กทม.)",
    howTitle: "ขั้นตอนการนัดหมาย 4 ขั้น",
    faqTitle: "คำถามที่พบบ่อย",
    relatedTitle: "บริการที่เกี่ยวข้อง",
    breadcrumb: ["หน้าหลัก", "Notary", "สินค้าและบริการ"],
    directAnswer: `ค่าบริการ ฿${product.priceMin.toLocaleString()}–฿${product.priceMax.toLocaleString()} · ${product.act.turnaround} · รวมค่าตราประทับ + บันทึกสมุดทะเบียน + คำแนะนำต่อ MFA / Apostille / สถานทูต`,
  } : {
    trust: `Certified by ${NOTARY_LEAD_ATTORNEY} — registered with the Lawyers Council of Thailand`,
    priceLabel: "Fee per set",
    turnLabel: "Turnaround",
    acceptedBy: "Accepted by",
    usedFor: "Used for",
    whatIncluded: "Included in this fee",
    tiers: "Booking options",
    tierStd: "Standard — 1 day", tierExp: "Express — same day", tierOn: "On-site visit",
    tierStdDesc: "Book one working day ahead — most popular", tierExpDesc: "Walk-in slots if available on the same day", tierOnDesc: "Attorney travels to hospital / hotel / office (Bangkok area)",
    howTitle: "How to book — 4 steps",
    faqTitle: "Frequently asked questions",
    relatedTitle: "Related services",
    breadcrumb: ["Home", "Notary", "Products"],
    directAnswer: `THB ${product.priceMin.toLocaleString()}–${product.priceMax.toLocaleString()} · ${product.act.turnaround} · Includes notarial seal, ledger entry, and MFA / Apostille / embassy next-step guidance.`,
  };

  const catalogPath = isTh ? "/notary/products" : "/en/notary/products";
  const notaryHubPath = isTh ? "/notary" : "/en/notary";

  return (
    <main className="min-h-screen bg-background pb-28 md:pb-16">
      <nav aria-label="Breadcrumb" className="mx-auto max-w-6xl px-4 pt-6 text-xs text-slate-500">
        <ol className="flex flex-wrap gap-1">
          <li><Link to={isTh ? "/" : "/en"} className="hover:text-indigo-700">{L.breadcrumb[0]}</Link></li>
          <li>›</li>
          <li><Link to={notaryHubPath} className="hover:text-indigo-700">{L.breadcrumb[1]}</Link></li>
          <li>›</li>
          <li><Link to={catalogPath} className="hover:text-indigo-700">{L.breadcrumb[2]}</Link></li>
          <li>›</li>
          <li aria-current="page" className="text-slate-700">{name}</li>
        </ol>
      </nav>

      <section className="mx-auto max-w-6xl px-4 pt-6">
        <div className="grid gap-8 lg:grid-cols-[1.15fr_1fr]">
          <div>
            <div className="mb-3 flex flex-wrap gap-2 text-[11px] font-bold uppercase tracking-widest">
              <span className="rounded-full bg-indigo-100 px-3 py-1 text-indigo-800">{catLabel}</span>
              <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-800">
                <ShieldCheck className="mr-1 inline h-3 w-3" /> Thai Bar Notary
              </span>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-700">SKU {product.sku}</span>
            </div>
            <h1 className="font-display text-3xl font-extrabold leading-tight text-navy-deep sm:text-4xl lg:text-5xl">
              {isTh ? `${name} — โดยทนาย Notarial Services` : `${name} — Thai Notarial Services`}
            </h1>
            <p data-speakable className="direct-answer mt-4 rounded-xl border-l-4 border-indigo-400 bg-indigo-50/50 p-4 text-[15px] leading-relaxed text-slate-700">
              {L.directAnswer}
            </p>

            <div className="price-band mt-5 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">{L.priceLabel}</div>
                <div className="mt-1 font-display text-2xl font-extrabold text-emerald-800">
                  THB {product.priceMin.toLocaleString()}–{product.priceMax.toLocaleString()}
                </div>
                <div className="mt-0.5 text-xs text-slate-600">{isTh ? "รวมตราประทับ + บันทึกสมุดทะเบียน" : "Seal + ledger entry included"}</div>
              </div>
              <div className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-indigo-700">{L.turnLabel}</div>
                <div className="mt-1 flex items-center gap-2 font-display text-2xl font-extrabold text-indigo-800">
                  <Clock className="h-5 w-5" /> {product.act.turnaround}
                </div>
                <div className="mt-0.5 text-xs text-slate-600">{isTh ? "เร่งด่วนภายในวันได้ (มีค่าธรรมเนียมเพิ่ม)" : "Same-day express available (surcharge)"}</div>
              </div>
            </div>

            <div className="mt-5">
              <NotaryProductCTA product={product} locale={locale} variant="inline" />
            </div>
            <div className="mt-3 text-xs text-slate-500">
              <ShieldCheck className="mr-1 inline h-3 w-3 text-emerald-600" />
              {L.trust}
            </div>
          </div>

          <aside className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-indigo-50/40 p-6">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-indigo-700">
              <Stamp className="h-4 w-4" /> {isTh ? "ตัวอย่างตราประทับ" : "Seal preview"}
            </div>
            <div className="mt-4 flex aspect-square items-center justify-center rounded-xl border-2 border-dashed border-indigo-300 bg-white p-8">
              <div className="text-center">
                <div className="mx-auto flex h-32 w-32 items-center justify-center rounded-full border-4 border-indigo-800 bg-white text-indigo-900">
                  <div className="text-center">
                    <Scale className="mx-auto h-6 w-6" />
                    <div className="mt-1 text-[8px] font-bold uppercase tracking-widest">Notarial</div>
                    <div className="text-[8px] font-bold uppercase tracking-widest">Services</div>
                    <div className="mt-1 border-t border-indigo-800 pt-1 text-[8px] font-bold">Thai Bar</div>
                  </div>
                </div>
                <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-500">
                  {isTh ? "ตราประทับสด + ลายเซ็นทนาย" : "Wet seal + attorney signature"}
                </div>
              </div>
            </div>
            <ul className="mt-5 space-y-1.5 text-[12px] text-slate-700">
              <li className="flex gap-2"><Globe2 className="h-3.5 w-3.5 text-indigo-600 mt-0.5" /> {isTh ? "ต่อ MFA / Apostille / สถานทูตได้ทันที" : "Ready for MFA / Apostille / embassy chain"}</li>
              <li className="flex gap-2"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600 mt-0.5" /> {isTh ? "จดทะเบียนสภาทนายความไทย ตรวจได้" : "Verifiable via Lawyers Council of Thailand"}</li>
              <li className="flex gap-2"><Check className="h-3.5 w-3.5 text-indigo-600 mt-0.5" /> {isTh ? "ตรวจต้นฉบับก่อนนัดเซ็นฟรี" : "Free source-document vetting before signing"}</li>
            </ul>
          </aside>
        </div>
      </section>

      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.whatIncluded}</h2>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {bullets.map((b) => (
            <li key={b} className="flex gap-3 rounded-xl border border-slate-200 bg-white p-4">
              <Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
              <span className="text-sm text-slate-700">{b}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">
          {isTh ? `เกี่ยวกับ ${name}` : `About ${name}`}
        </h2>
        <p className="mt-4 text-[15px] leading-relaxed text-slate-700">{longDesc}</p>
        <p className="mt-3 text-[15px] leading-relaxed text-slate-700">
          {isTh
            ? `เอกสารที่ต้องเตรียม: ${product.act.required.join(" · ")}`
            : `Documents to prepare: ${product.act.required.join(" · ")}`}
        </p>
      </section>

      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.tiers}</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {[
            { label: L.tierStd, desc: L.tierStdDesc, price: product.priceMin, tone: "emerald", badge: isTh ? "แนะนำ" : "Popular" },
            { label: L.tierExp, desc: L.tierExpDesc, price: product.priceMid, tone: "indigo" },
            { label: L.tierOn, desc: L.tierOnDesc, price: product.priceMax, tone: "amber" },
          ].map((t) => (
            <div key={t.label} className={`relative rounded-2xl border p-5 ${
              t.tone === "emerald" ? "border-emerald-300 bg-emerald-50/40 ring-2 ring-emerald-200"
              : t.tone === "indigo" ? "border-indigo-300 bg-indigo-50/40"
              : "border-amber-300 bg-amber-50/40"
            }`}>
              {t.badge && (
                <span className="absolute -top-2 right-4 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">
                  {t.badge}
                </span>
              )}
              <div className="font-display text-lg font-extrabold text-navy-deep">{t.label}</div>
              <div className="mt-1 text-xs text-slate-600">{t.desc}</div>
              <div className="mt-3 font-display text-2xl font-extrabold text-navy-deep">
                THB {t.price.toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.acceptedBy}</h2>
        <div className="mt-4 flex flex-wrap gap-2">
          {product.acceptedBy.map((a) => (
            <span key={a} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">
              ✓ {a}
            </span>
          ))}
        </div>
        {product.usedFor.length > 0 && (
          <>
            <h3 className="mt-6 font-display text-lg font-bold text-navy-deep">{L.usedFor}</h3>
            <div className="mt-2 flex flex-wrap gap-2">
              {product.usedFor.map((u) => (
                <span key={u} className="rounded-md bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold text-indigo-800 ring-1 ring-indigo-200">
                  {u}
                </span>
              ))}
            </div>
          </>
        )}
      </section>

      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.howTitle}</h2>
        <ol className="mt-4 grid gap-3 md:grid-cols-2">
          {steps.map((s, i) => (
            <li key={s} className="flex gap-3 rounded-xl border border-slate-200 bg-white p-4">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-700 font-display font-bold text-white">
                {i + 1}
              </span>
              <span className="text-sm text-slate-700">{s}</span>
            </li>
          ))}
        </ol>
      </section>

      <div className="mx-auto max-w-6xl px-4">
        <NotaryAuthorByline lang={locale} />
      </div>

      <section className="mx-auto mt-6 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.faqTitle}</h2>
        <div className="mt-4 space-y-3">
          {faqs.map((f) => (
            <details key={f.q} className="speakable-faq group rounded-xl border border-slate-200 bg-white p-4 open:bg-indigo-50/30">
              <summary className="cursor-pointer list-none font-display font-bold text-navy-deep">
                <span className="mr-2 text-indigo-600 group-open:hidden">+</span>
                <span className="mr-2 hidden text-indigo-600 group-open:inline">−</span>
                {f.q}
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-slate-700">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <CrossSellStrip source="notary" locale={locale} />

      <section className="mx-auto mt-12 max-w-6xl px-4">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{L.relatedTitle}</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((r) => (
            <Link
              key={r.sku}
              to={isTh ? "/notary/products/$slug" : "/en/notary/products/$slug"}
              params={{ slug: r.slug }}
              className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-indigo-400 hover:shadow-md"
            >
              <div className="text-[10px] font-bold uppercase tracking-widest text-indigo-700">
                {isTh ? r.categoryLabelTh : r.categoryLabelEn}
              </div>
              <div className="mt-1 font-display font-extrabold text-navy-deep group-hover:text-indigo-700">
                {isTh ? r.nameTh : r.nameEn}
              </div>
              <div className="mt-2 text-xs text-slate-500">
                THB {r.priceMin.toLocaleString()}–{r.priceMax.toLocaleString()} · {r.act.turnaround}
              </div>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-indigo-700">
                {isTh ? "ดูรายละเอียด" : "View details"} <ArrowRight className="h-3 w-3" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <NotaryProductCTA product={product} locale={locale} variant="sticky-mobile" />
      <ProductDepthBlock cluster="notary" locale={locale === "th" ? "th" : "en"} />
      <ConsultantAdvisoryBlock locale={locale === "th" ? "th" : "en"} topic="Notary Public" />
    </main>
  );
}
